import * as S from './DrawerListQuestion.styles';
import { useEffect, useState } from 'react';
import { CloseIcon } from '@app/components/common/icons/CloseIcon';
import { SearchIcon } from '@app/components/common/icons/SearchIcon';
import { Select, Option } from '@app/components/common/selects/Select/Select';
import { Pagination } from '@app/components/common/Pagination/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@app/store/store';
import { fetchExamList, setDifficult, setDone, setPage, setRepeated, setSearch } from '@app/store/slices/examSlice';
import { useNavigate } from 'react-router-dom';
import { convertToAbbreviation, convertTypeParctice } from '@app/utils/utils';
import useDebounce from '@app/hooks/useDebounce';
import { CheckIcon } from '@app/components/common/icons/CheckIcon';
import { useTranslation } from 'react-i18next';

interface DrawerListQuestionProps {
  open: boolean;
  toggle: () => void;
  loadListQuestion: (flag: boolean) => void;
  type: string;
}

export const DrawerListQuestion: React.FC<DrawerListQuestionProps> = ({ open, type, toggle, loadListQuestion }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { listExam, currentQuestion, page, totalPages, totalDone, itemCount, searchCode, repeated, difficult, done } =
    useSelector((state: RootState) => state.exam);
  const [activeTab, setActiveTab] = useState(repeated ? 'repeated' : 'all');
  const [level, setLevel] = useState(difficult);
  const [status, setStatus] = useState(done);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const debouncedSearch = useDebounce(searchCode, 500);
  const tabs = [
    { id: 'all', label: t('common.all') },
    { id: 'repeated', label: t('common.repeated') },
  ];

  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false);
      return;
    }
    loadListQuestion(false);
    dispatch(fetchExamList({ page: 1, type, searchCode: debouncedSearch, repeated, difficult, done }));
  }, [debouncedSearch, repeated, difficult, done]);

  useEffect(() => {
    dispatch(setRepeated(activeTab === 'repeated'));
  }, [activeTab, dispatch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearch(e.target.value));
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      dispatch(setPage(newPage));
      navigate(`/exam?type=${type}&page=${newPage}`);
    }
  };

  const handleExam = (examId: string) => {
    navigate(`/exam?type=${type}&id=${examId}&page=${page}`);
    toggle();
  };

  const handleLevelChange = (level: unknown) => {
    const selectedLevel = level as string;
    dispatch(setDifficult(selectedLevel));
  };

  const handleStatusChange = (status: unknown) => {
    const selectedStatus = status as string;
    dispatch(setDone(selectedStatus));
  };

  return (
    <>
      <S.Drawer open={open}>
        <S.BtnClose onClick={toggle}>
          <CloseIcon />
        </S.BtnClose>
        <S.DrawerContent>
          <S.ASQ>{convertToAbbreviation(type)}</S.ASQ>
          <p className="text-base font-bold my-2">{convertTypeParctice(type)}</p>
          <S.FormInput>
            <S.StyledInput
              type="text"
              placeholder={t('common.search')}
              value={searchCode}
              onChange={handleSearchChange}
            />
            <button>
              <SearchIcon />
            </button>
          </S.FormInput>
          <S.BoxTabs>
            <S.Tabs>
              {tabs.map((tab) => (
                <S.TabItem key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
                  {tab.label.toUpperCase()}
                </S.TabItem>
              ))}
            </S.Tabs>
            <div className="flex gap-4">
              <Select defaultValue={level} width={120} size={'small'} onChange={handleLevelChange}>
                <Option value="">{t('common.level.all')}</Option>
                <Option value="easy">{t('common.level.easy')}</Option>
                <Option value="medium">{t('common.level.medium')}</Option>
                <Option value="hard">{t('common.level.hard')}</Option>
                <Option value="unclassified">{t('common.level.unclassified')}</Option>
              </Select>
              <Select defaultValue={status} width={120} size={'small'} onChange={handleStatusChange}>
                <Option value="">{t('common.status.all')}</Option>
                <Option value="done">{t('common.status.done')}</Option>
                <Option value="notDone">{t('common.status.notDone')}</Option>
              </Select>
            </div>
          </S.BoxTabs>

          <S.Statistics>
            <span>{`${t('common.done')} ${totalDone}, ${t('common.found')} ${itemCount} ${t('common.question')}`}</span>
          </S.Statistics>

          <S.ListAnswer>
            {listExam.map((exam) => (
              <S.ItemAnswer
                key={exam.id}
                isCorrect={exam.id === currentQuestion?.id}
                onClick={() => handleExam(exam.id)}
              >
                <S.MainContent>
                  <span>{exam.orderId}</span>
                  {exam.topic}
                </S.MainContent>
                <S.SideContent>
                  <div className="flex gap-2 items-center">
                    <S.Level difficulty={exam.difficulty}>{exam.difficulty ? exam.difficulty : 'Unclassified'}</S.Level>
                    {exam.isdone && (
                      <S.Check>
                        <CheckIcon />
                      </S.Check>
                    )}
                  </div>
                </S.SideContent>
              </S.ItemAnswer>
            ))}
          </S.ListAnswer>

          <Pagination
            className="text-center"
            showSizeChanger={false}
            defaultCurrent={page}
            total={itemCount}
            pageSize={20}
            onChange={handlePageChange}
          />
        </S.DrawerContent>
      </S.Drawer>
      <S.Backdrop open={open} onClick={toggle}></S.Backdrop>
    </>
  );
};
